import { AnalysisTable } from "components/AnalysisTable/AnalysisTable";
import {
  AnalysisTableIdentifiers,
  ToolParamsGridRowDatum,
  ToolSpec,
} from "components/ToolParamsGrid/ToolParamsGrid.types";
import { ToolParamsGridProvider } from "components/ToolParamsGrid/ToolParamsGridProvider";
import {
  AnalysisTableGroup,
  Project,
  usePageProjectAnalysisQuery,
} from "graphql/_Types";
import { chain, clone, isUndefined } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { isNonNullish } from "utils/isNonNullish";
import { isNullish } from "utils/isNullish";
import { AnalysisTableLayoutProvider } from "./AnalysisTableLayoutProvider";
import {
  isAnalysisTableRowAttachments,
  isToolPathParam,
} from "components/ToolParamsGrid/ToolParamsGrid.helpers";
import { isNonNull } from "utils/isNonNull";
import { roundToSignificant } from "utils/roundToSignificant";
import { syncMetadatumReferences } from "components/ToolParamsGrid/ToolParamsGridProvider.helpers";
import { isDefined } from "utils/isDefined";
import { useGetProjectFilesByFileIds } from "components/ToolParamsGrid/hooks/useGetProjectFilesByFileIds";
import { ToolParamsGridValueValidatorContext } from "components/ToolParamsGrid/ToolParamsGridValueValidatorContext";
import { ToolParamsGridRowDataProvider } from "components/ToolParamsGrid/ToolParamsGridRowDataProvider";
import { ErrorBoundary } from "@sentry/react";
import { PageProjectAnalysisError } from "./PageProjectAnalysisError";
import { ResourceTier } from "types/ResourceTiers";
import { ModalProvider } from "providers/ModalProvider/ModalProvider";
import { coalesceToStringArray } from "utils/coalesceToStringArray";
import AppLoading from "components/AppLoading/AppLoading";

interface PageProjectAnalysisProps {
  projectId: Project["id"];
  projectKey: Project["key"];
  analysisTableGroupId: AnalysisTableGroup["id"];
}

export const PageProjectAnalysis = ({
  projectId,
  projectKey,
  analysisTableGroupId,
}: PageProjectAnalysisProps) => {
  const [initialRowData, setInitialRowData] =
    useState<ToolParamsGridRowDatum[]>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error>();
  const [selectedTableId, setSelectedTableId] = useState<string>();
  const { getProjectFilesByFileIds } = useGetProjectFilesByFileIds();
  const { data, refetch } = usePageProjectAnalysisQuery({
    variables: {
      analysisTableGroupId,
    },
    onError: setError,
    fetchPolicy: "network-only",
  });

  /**
   * Callback triggered when analysis table "tab" is selected
   */
  const handleClickTab = useCallback(
    async (tableId: string) => {
      if (selectedTableId !== tableId) {
        // Apply any changes made in the previous tab to the query data
        try {
          await refetch();
          setSelectedTableId(tableId);
          setInitialRowData(undefined);
        } catch (error) {
          setError(error as Error);
        }
      }
    },
    [refetch, selectedTableId],
  );

  const group = data?.analysisTableGroup;
  const analysisTableGroup = isNonNullish(group)
    ? {
        ...group,
        usedCredits: roundToSignificant(group.usedCredits ?? 0),
      }
    : undefined;
  const tool = analysisTableGroup?.tool;

  // Initialize the selectable analysis table ID state
  useEffect(() => {
    if (selectedTableId === undefined) {
      const newestTable = chain(analysisTableGroup?.analysisTables.nodes)
        .sortBy(({ dateCreated }) => dateCreated)
        .last()
        .value();

      setSelectedTableId(newestTable?.id);
    }
  }, [analysisTableGroup?.analysisTables.nodes, selectedTableId]);

  const analysisTables = useMemo(() => {
    return clone(analysisTableGroup?.analysisTables.nodes ?? [])
      .sort((a, b) => (a.dateCreated > b.dateCreated ? -1 : 1))
      .map((analysisTable) => {
        const toolSpec = analysisTable.toolVersion?.toolSpec;

        const toolVersion = analysisTable.toolVersion;

        const toolVersions =
          analysisTable.toolVersion?.compatibleVersions.nodes;

        if (
          isNullish(analysisTable) ||
          isNullish(analysisTable.name) ||
          isNullish(toolVersion) ||
          isNullish(toolSpec) ||
          isNullish(toolVersions)
        ) {
          return undefined;
        }

        return {
          ...analysisTable,
          toolVersion,
          name: analysisTable.name,
          identifiers:
            analysisTable.identifiers as AnalysisTableIdentifiers | null,
          toolSpec: {
            ...(toolSpec as ToolSpec),
            credits: toolVersion.credits ?? 0,
          },
          toolVersions,
        };
      })
      .filter(isDefined);
  }, [analysisTableGroup?.analysisTables.nodes]);

  const analysisTable = analysisTables.find(
    (table) => table.id === selectedTableId,
  );

  useEffect(() => {
    const pipelineTableRows = analysisTable?.analysisTableRows.nodes;

    if (
      analysisTable === undefined ||
      pipelineTableRows === undefined ||
      isDefined(initialRowData)
    ) {
      return;
    }

    const rowData = pipelineTableRows
      .map((row) => {
        if (row.toolVersion === null) {
          return undefined;
        }

        const { recordingsIds, datasetsIds } = (() => {
          const attachments = row?.attachments as unknown;
          // Read dataset and recording references from the row attachments for
          // executed rows
          if (row.taskId !== null) {
            if (isAnalysisTableRowAttachments(attachments)) {
              return {
                recordingsIds:
                  attachments.recordings.length > 0
                    ? attachments.recordings
                    : undefined,
                datasetsIds:
                  attachments.datasets.length > 0
                    ? attachments.datasets
                    : undefined,
              };
            }
            return { recordingsIds: undefined, datasetsIds: undefined };
          }

          // Recalculate dataset and recording references based on file IDs
          // stored in tool path param values
          return chain(analysisTable.toolSpec.params)
            .filter(isToolPathParam)
            .flatMap((param) => {
              const rowDatum = (row.data ??
                {}) as ToolParamsGridRowDatum["params"];
              const fileIds = rowDatum[param.key];
              return coalesceToStringArray(fileIds);
            })
            .thru((fileIds) => {
              const { drsFilesFound: files } =
                getProjectFilesByFileIds(fileIds);
              const datasetsIds = chain(files)
                .flatMap((file) => file.datasets)
                .map((dataset) => dataset.id)
                .uniq()
                .value();
              const recordingsIds = chain(files)
                .flatMap((file) => file.recordings)
                .map((recording) => recording.id)
                .uniq()
                .value();
              return { datasetsIds, recordingsIds };
            })
            .value();
        })();

        const rowDatum: ToolParamsGridRowDatum = {
          id: row.id,
          task_id: row.task?.id,
          task_user_id: row.task?.userId,
          task_status: row.task?.status,
          task_date_created: row.task?.created,
          task_duration: row.task?.taskActivityByTaskId?.duration,
          task_compute_credit: row.task?.credits,
          task_cloned: row.task?.cloned,
          output_group_files: row.task?.outputGroup?.outputGroupFiles.nodes
            .map((node) => node.drsFile)
            .filter(isNonNull),
          params:
            (row.data as ToolParamsGridRowDatum["params"] | undefined) ?? {},
          recordings: recordingsIds,
          datasets: datasetsIds,
          selected: false,
          selections: row.selections as
            | ToolParamsGridRowDatum["selections"]
            | undefined,
          attachResults: row.activeAttachResults ?? true,
          metadatumReferences: (row.metadatumReferences ??
            {}) as ToolParamsGridRowDatum["metadatumReferences"],
          toolVersion: row.toolVersion,
          resource_tier: row.resourceTier as ResourceTier["id"] | null,
        };

        return rowDatum;
      })
      .filter(isDefined);

    syncMetadatumReferences(rowData)
      .then(setInitialRowData)
      .catch(setError)
      .finally(() => setIsLoading(false));
  }, [analysisTable, getProjectFilesByFileIds, initialRowData]);

  if (isLoading) {
    return <AppLoading />;
  }

  if (
    error ||
    isNullish(analysisTableGroup) ||
    isNullish(analysisTable) ||
    isUndefined(initialRowData) ||
    isNullish(tool)
  ) {
    return <PageProjectAnalysisError />;
  }

  return (
    <ErrorBoundary fallback={<PageProjectAnalysisError />}>
      <ToolParamsGridProvider
        // Specifying a key prop forces the provider to remount any time new
        // table is selected
        key={selectedTableId}
        analysisTableGroup={analysisTableGroup}
        analysisTable={analysisTable}
        toolSpec={analysisTable.toolSpec}
        toolId={tool.id}
        toolVersions={analysisTable.toolVersions}
      >
        <ToolParamsGridRowDataProvider
          projectId={projectId}
          initialRowData={initialRowData}
          analysisTable={analysisTable}
          toolSpec={analysisTable.toolSpec}
          projectKey={projectKey}
        >
          <AnalysisTableLayoutProvider>
            <ToolParamsGridValueValidatorContext>
              <ModalProvider>
                <AnalysisTable
                  group={analysisTableGroup}
                  tables={analysisTables}
                  selectedTableId={selectedTableId}
                  setSelectedTableId={handleClickTab}
                  tool={tool}
                />
              </ModalProvider>
            </ToolParamsGridValueValidatorContext>
          </AnalysisTableLayoutProvider>
        </ToolParamsGridRowDataProvider>
      </ToolParamsGridProvider>
    </ErrorBoundary>
  );
};
